import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Upload, FileCheck, AlertCircle, CheckCircle, Clock, Shield, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'national_id', label: 'National ID Card' },
  { value: 'drivers_license', label: "Driver's License" },
];

const STATUS_DISPLAY = {
  pending_documents: { label: 'Pending Documents', color: 'bg-amber-500/10 text-amber-400', icon: Clock },
  under_review: { label: 'Under Review', color: 'bg-sky-500/10 text-sky-400', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-500/10 text-emerald-400', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500/10 text-red-400', icon: AlertCircle },
};

function FileUploadBox({ label, required, accept, file, onFileChange, onRemove }) {
  const inputRef = React.useRef();
  return (
    <div data-testid={`upload-box-${label.toLowerCase().replace(/\s+/g, '-')}`} className="border-2 border-dashed border-zinc-700 rounded-xl p-5 hover:border-sky-500/50 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-zinc-200">
          {label} {required && <span className="text-red-400">*</span>}
        </label>
        {file && (
          <button onClick={onRemove} className="text-zinc-500 hover:text-red-400">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {file ? (
        <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
          <FileCheck className="w-5 h-5 text-emerald-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-zinc-200 truncate">{file.name}</p>
            <p className="text-xs text-zinc-500">{(file.size / 1024).toFixed(0)} KB</p>
          </div>
        </div>
      ) : (
        <div
          className="flex flex-col items-center gap-2 p-4 cursor-pointer"
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="w-8 h-8 text-zinc-500" />
          <p className="text-sm text-zinc-400">Click to upload</p>
          <p className="text-xs text-zinc-600">JPG, PNG, PDF, WEBP — Max 5MB</p>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept || "image/*,.pdf"}
        className="hidden"
        onChange={(e) => onFileChange(e.target.files[0])}
      />
    </div>
  );
}

export default function KycVerify() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form state
  const [documentType, setDocumentType] = useState('national_id');
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [country, setCountry] = useState('');
  const [files, setFiles] = useState({ id_front: null, id_back: null, proof_of_address: null, selfie: null });

  const validateToken = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/kyc/verify/${token}`);
      setTokenData(res.data);
      if (res.data.already_submitted) {
        setSubmitted(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired verification link');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.id_front) {
      setError('Government ID (front) is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    const formData = new FormData();
    formData.append('id_front', files.id_front);
    if (files.id_back) formData.append('id_back', files.id_back);
    if (files.proof_of_address) formData.append('proof_of_address', files.proof_of_address);
    if (files.selfie) formData.append('selfie', files.selfie);
    formData.append('document_type', documentType);
    formData.append('full_name', fullName);
    formData.append('date_of_birth', dateOfBirth);
    formData.append('country', country);

    try {
      await axios.post(`${API_URL}/api/kyc/upload/${token}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  if (error && !tokenData) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-zinc-100 mb-2">Verification Link Error</h2>
            <p className="text-zinc-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    const statusInfo = STATUS_DISPLAY[tokenData?.submission_status || 'under_review'] || STATUS_DISPLAY.under_review;
    const StatusIcon = statusInfo.icon;
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
          <CardContent className="p-8 text-center">
            <StatusIcon className="w-16 h-16 text-sky-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-zinc-100 mb-2">Documents Submitted</h2>
            <p className="text-zinc-400 mb-4">Your identity verification documents have been received.</p>
            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
            <p className="text-sm text-zinc-500 mt-4">You will receive an email once the review is complete.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Shield className="w-8 h-8 text-sky-400" />
            <h1 className="text-2xl font-bold text-zinc-100">EzyTransfer</h1>
          </div>
          <h2 className="text-lg text-zinc-300">Identity Verification</h2>
          <p className="text-sm text-zinc-500 mt-1">Hello {tokenData?.user_name}, please upload your documents below</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Info */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-base text-zinc-200">Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Full Name (as on ID) *</label>
                  <input
                    data-testid="kyc-full-name"
                    type="text"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Date of Birth</label>
                  <input
                    data-testid="kyc-dob"
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Country</label>
                  <input
                    data-testid="kyc-country"
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none"
                    placeholder="e.g. Somalia, Kenya, USA"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Document Type *</label>
                  <select
                    data-testid="kyc-doc-type"
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none"
                  >
                    {DOCUMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Document Uploads */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-base text-zinc-200">Upload Documents</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FileUploadBox
                label="Government ID - Front"
                required
                file={files.id_front}
                onFileChange={(f) => setFiles((prev) => ({ ...prev, id_front: f }))}
                onRemove={() => setFiles((prev) => ({ ...prev, id_front: null }))}
              />
              <FileUploadBox
                label="Government ID - Back"
                file={files.id_back}
                onFileChange={(f) => setFiles((prev) => ({ ...prev, id_back: f }))}
                onRemove={() => setFiles((prev) => ({ ...prev, id_back: null }))}
              />
              <FileUploadBox
                label="Proof of Address"
                file={files.proof_of_address}
                onFileChange={(f) => setFiles((prev) => ({ ...prev, proof_of_address: f }))}
                onRemove={() => setFiles((prev) => ({ ...prev, proof_of_address: null }))}
              />
              <FileUploadBox
                label="Selfie with ID"
                accept="image/*"
                file={files.selfie}
                onFileChange={(f) => setFiles((prev) => ({ ...prev, selfie: f }))}
                onRemove={() => setFiles((prev) => ({ ...prev, selfie: null }))}
              />
            </CardContent>
          </Card>

          {error && (
            <div data-testid="kyc-error" className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <Button
            data-testid="kyc-submit-btn"
            type="submit"
            disabled={submitting || !files.id_front}
            className="w-full bg-sky-500 hover:bg-sky-600 text-white py-3"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading & Analyzing...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Submit Documents for Verification</>
            )}
          </Button>

          <p className="text-xs text-zinc-600 text-center">
            Your documents are encrypted and stored securely. They will only be used for identity verification purposes.
          </p>
        </form>
      </div>
    </div>
  );
}
